/**
 * The pieces the four Asset Management screens share: figures, the coverage
 * line every screen leads with, health and stage chips, and two small charts.
 */
import type { ReactNode } from "react";
import { BAND_COLOUR, BAND_LABEL, STAGE_LABEL, type LifecycleStage } from "../../domain/assetManagement";
import { t } from "../../i18n";
import s from "./AssetManagement.module.css";

export { s };

export const SUITE = "Asset Management";

export function money(v: number | null | undefined, currency = "SAR"): string {
  if (v == null) return "—";
  const abs = Math.abs(v);
  const n =
    abs >= 1e6
      ? `${(v / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`
      : abs >= 1e4
        ? `${Math.round(v / 1e3)}k`
        : Math.round(v).toLocaleString("en");
  return `${currency} ${n}`;
}

export function pct(v: number | null | undefined): string {
  return v == null ? "—" : `${v}%`;
}

export function day(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : "—";
}

export interface Tile {
  key: string;
  label: string;
  value: ReactNode;
  unit?: string;
  sub?: string;
  tone?: "good" | "warn" | "bad";
}

export function Tiles({ items }: { items: Tile[] }) {
  return (
    <section className={s.tiles}>
      {items.map((f) => (
        <div key={f.key} className={s.tile} data-tone={f.tone} data-testid={`am-tile-${f.key}`}>
          <div className={s.tileN}>
            {f.value}
            {f.unit && <small>{f.unit}</small>}
          </div>
          <div className={s.tileLabel}>{f.label}</div>
          {f.sub && <div className={s.tileSub}>{f.sub}</div>}
        </div>
      ))}
    </section>
  );
}

/** How much of the register a screen's numbers stand on. Always shown, never hidden. */
export function Coverage({ lines }: { lines: { label: string; of: number; n: number }[] }) {
  return (
    <div className={s.coverage} data-testid="am-coverage">
      {lines.map((l) => {
        const share = l.of ? Math.round((l.n / l.of) * 100) : 0;
        return (
          <div key={l.label}>
            <div>
              <b>{t("{n} of {of}", { n: l.n.toLocaleString("en"), of: l.of.toLocaleString("en") })}</b> {l.label} (
              {share}%)
            </div>
            <div className={s.meter}>
              <span style={{ width: `${share}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function HealthChip({ band, score }: { band: number | null; score?: number | null }) {
  if (band == null) return <span className={s.dim}>{t("Not assessed")}</span>;
  return (
    <span className={s.chip} style={{ ["--chip" as string]: BAND_COLOUR[band] }}>
      {t(BAND_LABEL[band])}
      {score != null && <span className={s.dim}>{score}</span>}
    </span>
  );
}

export function StageLabel({ stage }: { stage: string }) {
  return <>{t(STAGE_LABEL[stage as LifecycleStage] ?? stage)}</>;
}

export function Criticality({ value }: { value: number }) {
  return (
    <span className={s.mono} title={t("Criticality {n} of 5", { n: value })}>
      {"■".repeat(value)}
      <span className={s.dim}>{"□".repeat(5 - value)}</span>
    </span>
  );
}

export function PolicyTag({ source }: { source: string | null | undefined }) {
  if (source !== "policy") return null;
  return (
    <span className={`${s.tag} ${s.tagPolicy}`} title={t("From the asset-type policy, not recorded on this asset")}>
      {t("policy")}
    </span>
  );
}

/**
 * Columns with their value printed on each — one series, so no legend; an
 * optional marker per column (the funded amount) drawn as a tick.
 */
export function ColumnChart({
  data,
  format,
  marker,
  markerLabel,
  label,
}: {
  data: { key: string; label: string; value: number; marker?: number | null }[];
  format: (v: number) => string;
  marker?: boolean;
  markerLabel?: string;
  label: string;
}) {
  const W = 720;
  const H = 230;
  const top = 22;
  const bottom = 28;
  const max = Math.max(1, ...data.map((d) => Math.max(d.value, d.marker ?? 0)));
  const slot = (W - 20) / Math.max(1, data.length);
  const bw = Math.min(46, slot * 0.6);
  const y = (v: number) => top + (H - top - bottom) * (1 - v / max);
  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} className={s.chart} role="img" aria-label={label}>
        <line x1={10} x2={W - 10} y1={H - bottom} y2={H - bottom} stroke="var(--line)" />
        {data.map((d, i) => {
          const x = 10 + i * slot + (slot - bw) / 2;
          return (
            <g key={d.key}>
              <title>{`${d.label}: ${format(d.value)}${d.marker != null ? ` · ${markerLabel}: ${format(d.marker)}` : ""}`}</title>
              <rect
                x={x}
                y={y(d.value)}
                width={bw}
                height={Math.max(0, H - bottom - y(d.value))}
                rx={3}
                fill="var(--blue)"
              />
              {d.value > 0 && (
                <text x={x + bw / 2} y={y(d.value) - 5} textAnchor="middle" fontSize={11} fill="var(--ink)">
                  {format(d.value)}
                </text>
              )}
              {marker && d.marker != null && (
                <line
                  x1={x - 6}
                  x2={x + bw + 6}
                  y1={y(d.marker)}
                  y2={y(d.marker)}
                  stroke="var(--amber)"
                  strokeWidth={2.5}
                  strokeDasharray="4 3"
                />
              )}
              <text x={x + bw / 2} y={H - 10} textAnchor="middle" fontSize={11} fill="var(--muted)">
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
      {marker && markerLabel && (
        <figcaption className={s.note}>
          <span style={{ color: "var(--amber)" }}>- - -</span> {markerLabel}
        </figcaption>
      )}
    </figure>
  );
}

/** A 0–100 line, for a health trend. */
export function TrendLine({ points, label }: { points: { label: string; value: number }[]; label: string }) {
  const W = 720;
  const H = 180;
  const pad = 26;
  if (!points.length) return null;
  const x = (i: number) => pad + (i * (W - 2 * pad)) / Math.max(1, points.length - 1);
  const y = (v: number) => 12 + (H - 40) * (1 - v / 100);
  const path = points.map((p, i) => `${i ? "L" : "M"}${x(i)},${y(p.value)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={s.chart} role="img" aria-label={label}>
      {[20, 40, 60, 80, 100].map((g) => (
        <g key={g}>
          <line x1={pad} x2={W - pad} y1={y(g)} y2={y(g)} stroke="var(--line)" strokeWidth={0.8} />
          <text x={pad - 6} y={y(g) + 4} textAnchor="end" fontSize={10} fill="var(--dim)">
            {g}
          </text>
        </g>
      ))}
      <path d={path} fill="none" stroke="var(--blue)" strokeWidth={2} />
      {points.map((p, i) => (
        <g key={p.label}>
          <title>{`${p.label}: ${p.value}`}</title>
          <circle cx={x(i)} cy={y(p.value)} r={4} fill="var(--blue)" stroke="var(--panel)" strokeWidth={2} />
          <text x={x(i)} y={H - 8} textAnchor="middle" fontSize={10.5} fill="var(--muted)">
            {p.label}
          </text>
        </g>
      ))}
      <text
        x={x(points.length - 1)}
        y={y(points[points.length - 1].value) - 9}
        textAnchor="middle"
        fontSize={11}
        fill="var(--ink)"
      >
        {points[points.length - 1].value}
      </text>
    </svg>
  );
}

export const KIND_LABEL: Record<string, string> = {
  preventive: "Preventive",
  predictive: "Predictive",
  inspection: "Inspection",
  corrective: "Corrective",
  project: "Project",
  out_of_scope: "Out of scope",
};

export const DEFECT_STATUS_LABEL: Record<string, string> = {
  open: "Open",
  triaged: "Triaged",
  scheduled: "Scheduled",
  resolved: "Resolved",
  rejected: "Rejected",
};

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
